import { giphyDarkestGrey } from '@giphy/colors'
import Header from 'modules/header/components/header'
import { useContext, useEffect, useState } from 'react'
import DotsLoader from 'shared/components/loaders/dots'
import UserContext from 'shared/contexts/user'
import { trackPageView } from 'shared/util/analytics'
import styled from 'styled-components'
import { SUPER_CUT_DEFAULT_GET_CAPTURE_LIMIT } from '../../../constants'
import { CreationToolsContext } from '../../../manager'
import { creationToolPeekerUrl } from '../../../types/creation-tool'
import CreationToolError from '../../../types/error'
import CreationToolGradient from '../../../types/gradient'
import { ContainerBlock, WrapperBlock } from '../../blocks'
import { GradientButton } from '../../buttons'
import CreationToolsErrorModal from '../../error-modal'
import SegmentedControl from '../../segmented-control'
import Copy from '../../welcome/copy'
import Gradient from '../../welcome/copy/gradient'
import Footer from '../shared/components/footer'
import SupportCTA from '../shared/components/support-cta'
import { ICapture, IPending } from '../shared/types'
import API from '../shared/utils/api'
import watcher from '../shared/utils/capture-watcher'
import CaptureTable from './capture-table'
import NewCapture from './new-capture'
import Welcome from './welcome'

const api = new API()

const TabsBar = styled.div`
    padding: 40px 0 10px 0;
    width: 30%;
`

const Container = styled(ContainerBlock as any)`
    background: none;
    padding: 80px 80px 40px;
`

const Content = styled.div`
    position: relative;
    width: 100%;
`

const Peeker = styled.img`
    left: 50%;
    margin-left: -90px;
    pointer-events: none;
    position: absolute;
    top: 0;
    transform: translateY(-50%) scaleX(-1);
    width: 180px;
`

const LoaderContainer = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    min-height: 250px;
`
const TabSlider = styled(SegmentedControl)`
    background: ${giphyDarkestGrey};
`

const LoadMoreButton = styled(GradientButton)`
    border-radius: 2px;
    height: 36px;
    width: 220px;
    margin-top: 28px;
`

const ErrorContainer = styled.div`
    margin: 100px auto;
    width: 50%;
`

const Home = () => {
    const { tool } = useContext(CreationToolsContext)
    const { user } = useContext(UserContext)

    const [error, setError] = useState<CreationToolError>()
    const [captures, setCaptures] = useState([] as ICapture[])
    const [pending, setPending] = useState([] as IPending[])
    const [allCaptures, setAllCaptures] = useState([] as ICapture[])
    const [allPending, setAllPending] = useState([] as IPending[])

    const [isLoading, setIsLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('mine')
    const peekerUrl = creationToolPeekerUrl(tool)

    const [myCaptureOffset, setMyCaptureOffset] = useState(0)
    const [allCaptureOffset, setAllCaptureOffset] = useState(0)
    const [showMyCaptureLoadMore, setShowMyCaptureLoadMore] = useState(true)
    const [showAllCaptureLoadMore, setShowAllCaptureLoadMore] = useState(true)
    const [isPaginationLoading, setIsPaginationLoading] = useState(false)

    const showLoadMore =
        (showMyCaptureLoadMore && activeTab === 'mine') || (showAllCaptureLoadMore && activeTab === 'all')

    useEffect(() => {
        trackPageView('/supercut')
        fetchCaptures()
        watcher.addListener(onCaptureComplete)

        return () => {
            watcher.removeListener(onCaptureComplete)
        }
    }, [])

    const onCaptureComplete = () => {
        fetchCaptures()
    }

    const setTab = (index: number) => {
        if (index === 0) {
            setActiveTab('mine')
        } else if (index === 1) {
            setActiveTab('all')
        }
    }

    const fetchCaptures = async () => {
        try {
            await fetchMyCaptures()
        } catch (err) {
            setShowMyCaptureLoadMore(false)
            setError(CreationToolError.SuperCutLoadFailed)
            setIsLoading(false)
            return
        }

        if (user.isStaff) {
            try {
                await fetchStaffCaptures()
            } catch (err) {
                setError(CreationToolError.SuperCutStaffLoadFailed)
            }
        }

        setIsLoading(false)
    }

    const fetchMyCaptures = async () => {
        try {
            const newCaptures = await api.getCaptures(myCaptureOffset)
            setCaptures(captures.concat(newCaptures.captures))
            setPending(newCaptures.pending) // DVR only ever returns the last 10 pending, so no need to concat here.
            setMyCaptureOffset(myCaptureOffset + SUPER_CUT_DEFAULT_GET_CAPTURE_LIMIT)
            if (newCaptures.captures.length < SUPER_CUT_DEFAULT_GET_CAPTURE_LIMIT) {
                setShowMyCaptureLoadMore(false)
            }
        } catch (err) {
            setShowMyCaptureLoadMore(false)
            setError(CreationToolError.SuperCutLoadFailed)
            console.error('Failed to get captures', err)
        }
    }

    const fetchStaffCaptures = async () => {
        try {
            const newCaptures = await api.getAllCaptures(allCaptureOffset)
            setAllCaptures(allCaptures.concat(newCaptures.captures))
            setAllPending(newCaptures.pending) // DVR only ever returns the last 10 pending, so no need to concat here.
            setAllCaptureOffset(allCaptureOffset + SUPER_CUT_DEFAULT_GET_CAPTURE_LIMIT)
            if (newCaptures.captures.length < SUPER_CUT_DEFAULT_GET_CAPTURE_LIMIT) {
                setShowAllCaptureLoadMore(false)
            }
        } catch (err) {
            setShowMyCaptureLoadMore(false)
            setError(CreationToolError.SuperCutStaffLoadFailed)
            console.error('Failed to get staff captures', err)
        }
    }

    const onLoadMoreClick = async () => {
        setIsPaginationLoading(true)
        if (activeTab === 'mine') {
            await fetchMyCaptures()
        } else if (activeTab === 'all') {
            await fetchStaffCaptures()
        }
        setIsPaginationLoading(false)
    }

    const onCaptureAdded = (uuid: string) => {
        watcher.add(uuid)
        fetchCaptures()
    }

    const onCaptureDeleted = (id: string) => {
        setCaptures((state) => state.filter((item) => item.id !== id))
        if (user.isStaff) {
            setAllCaptures((state) => state.filter((item) => item.id !== id))
        }
    }

    return (
        <>
            <Header />
            <WrapperBlock>
                <Container>
                    <Gradient />
                    <Content>
                        <Copy />
                        {isLoading ? (
                            <LoaderContainer>
                                <DotsLoader />
                            </LoaderContainer>
                        ) : (
                            <>
                                {error ? (
                                    <ErrorContainer>
                                        <CreationToolsErrorModal
                                            error={error}
                                            onGoBack={() => {
                                                window.location.reload()
                                            }}
                                            goBackLabel="Try Again"
                                        />
                                    </ErrorContainer>
                                ) : (
                                    <>
                                        {captures.length === 0 && <Welcome />}
                                        <NewCapture onCaptureAdded={onCaptureAdded} />
                                        {user.isStaff && (
                                            <TabsBar>
                                                <TabSlider
                                                    items={['My Captures', 'All Captures']}
                                                    onIndexChange={setTab}
                                                    gradient={CreationToolGradient.IndigoBlue}
                                                />
                                            </TabsBar>
                                        )}
                                        <CaptureTable
                                            captures={activeTab === 'mine' ? captures : allCaptures}
                                            pending={activeTab === 'mine' ? pending : allPending}
                                            isAllCaptures={activeTab !== 'mine'}
                                            onCaptureDeleted={(id: string) => onCaptureDeleted(id)}
                                        />
                                        {showLoadMore && (
                                            <div style={{ margin: '40px auto 0px auto', display: 'table' }}>
                                                {isPaginationLoading ? (
                                                    <DotsLoader />
                                                ) : (
                                                    <LoadMoreButton
                                                        colors={CreationToolGradient.colors(
                                                            CreationToolGradient.IndigoPurple
                                                        )}
                                                        onClick={onLoadMoreClick}
                                                    >
                                                        Load More...
                                                    </LoadMoreButton>
                                                )}
                                            </div>
                                        )}
                                        <SupportCTA />
                                    </>
                                )}
                            </>
                        )}
                        <Footer />
                    </Content>
                    {peekerUrl && <Peeker src={peekerUrl} alt="" />}
                </Container>
            </WrapperBlock>
        </>
    )
}

export default Home
